import { EntityTemplateField, EntityTemplateStep, isLegalEntityStandardField, LegalEntityStandardField } from "../../entityTemplates/EntityTemplate.types";
import { LegalEntityFieldModel } from "../../entityTemplates/LegalEntityFieldRenderers";
import { hasFieldData } from "../../entityTemplates/fieldValue";
import { ValidationError } from "@common/validationError";
import { isNullOrWhitespace } from "@common/stringOperations";
import { useTranslation } from "@localization/useTranslation";
import { useResponsibleQuery } from "../../user/hooks/useResponsibleQuery";
import { useTagsMultiSelectAutoUpdater } from "@common/components/tagsMultiSelect/tagsMultiSelectAutoUpdater/TagsMultiSelectAutoUpdater.hooks";
import { TagRelationType } from "@common/components/tagsMultiSelect/TagsMultiSelect.types";
import { exhaustiveGuard } from "@common/utilities";

export function useValidateLegalEntityStep(legalEntityId: string) {
    const { translateString } = useTranslation();
    const { selectedTags } = useTagsMultiSelectAutoUpdater(legalEntityId, TagRelationType.LegalEntity, false);
    const { userData } = useResponsibleQuery();

    function validateStep(step: EntityTemplateStep, legalEntity: LegalEntityFieldModel) {
        const result: Array<ValidationError> = [];

        step.fields.forEach((field) => {
            const fieldValidation = validateValue(getLegalEntityFieldValue(field, legalEntity, selectedTags), field, legalEntity);
            if (fieldValidation) {
                result.push(fieldValidation);
            }
        });

        return result;
    }

    function validateValue(value: any, field: EntityTemplateField, legalEntity: LegalEntityFieldModel): ValidationError | null {
        const fieldName = getFieldName(field);

        if (field.required && !hasFieldData(value)) {
            return new ValidationError(fieldName, translateString("requiredField"));
        }

        return fieldCustomValidation(value, fieldName, legalEntity, field);
    }

    function fieldCustomValidation(value: any, fieldName: string, legalEntity: LegalEntityFieldModel, field: EntityTemplateField): ValidationError | null {
        if (field.standardFieldIdentifier === LegalEntityStandardField.LegalEntityName && isNullOrWhitespace(value)) {
            return new ValidationError(fieldName, translateString("nameRequired"));
        }

        if (field.standardFieldIdentifier === LegalEntityStandardField.LegalEntityResponsible) {
            const user = userData?.find((x) => x.id === legalEntity.responsibleId);
            if (user && !user.active) {
                return new ValidationError(fieldName, translateString("userDeactivated"));
            }
        }

        return null;
    }

    return { validateStep, selectedTags };
}

export function getFieldName(field: EntityTemplateField) {
    return field.standardFieldIdentifier ? LegalEntityStandardField[field.standardFieldIdentifier].toString() : field.id!;
}

export function getLegalEntityFieldValue(field: EntityTemplateField, legalEntity: Partial<LegalEntityFieldModel>, selectedTags: Array<string>) {
    if (field.standardFieldIdentifier && isLegalEntityStandardField(field.standardFieldIdentifier)) {
        switch (field.standardFieldIdentifier) {
            case LegalEntityStandardField.LegalEntityName:
                return legalEntity.name;
            case LegalEntityStandardField.LegalEntityDescription:
                return legalEntity.description;
            case LegalEntityStandardField.LegalEntityReferenceNumber:
                return legalEntity.referenceNumber;
            case LegalEntityStandardField.LegalEntityNote:
                return legalEntity.note;
            case LegalEntityStandardField.LegalEntityAddress:
                return legalEntity.address;
            case LegalEntityStandardField.LegalEntityZipCode:
                return legalEntity.zipCode;
            case LegalEntityStandardField.LegalEntityCity:
                return legalEntity.city;
            case LegalEntityStandardField.LegalEntityCountry:
                return legalEntity.country;
            case LegalEntityStandardField.LegalEntityCompanyRegistrationNumber:
                return legalEntity.companyRegistrationNumber;
            case LegalEntityStandardField.LegalEntityWebsite:
                return legalEntity.website;
            case LegalEntityStandardField.LegalEntityTypes:
                return legalEntity.types;
            case LegalEntityStandardField.LegalEntityApproved:
                return legalEntity.isApproved;
            case LegalEntityStandardField.LegalEntityStatus:
                return legalEntity.isActive;
            case LegalEntityStandardField.LegalEntityBusinessAreas:
                return legalEntity.businessAreaIds;
            case LegalEntityStandardField.LegalEntityTags:
                return selectedTags;
            case LegalEntityStandardField.LegalEntityResponsible:
                return legalEntity.responsibleId;
            case LegalEntityStandardField.LegalEntitySubContractors:
                return legalEntity.subContractorIds;
            case LegalEntityStandardField.LegalEntityAuditType:
                return legalEntity.auditType;
            case LegalEntityStandardField.LegalEntityAuditFrequency:
                return legalEntity.auditFrequency;
            case LegalEntityStandardField.LegalEntityComplianceAreas:
                return legalEntity.complianceAreaIds;
            case LegalEntityStandardField.LegalEntityCertifications:
                return legalEntity.certificationIds;
            default:
                return exhaustiveGuard(field.standardFieldIdentifier);
        }
    }

    return legalEntity.customFieldValues?.find((x) => x.customInputFieldId === field.customInputFieldId)?.value;
}
